import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Building2, CalendarDays } from "lucide-react";
import { useGymQuery } from "./gym/hooks/useGymQuery";
import { useGymState } from "./gym/hooks/useGymState";
import { useGymMutations } from "./gym/hooks/useGymMutations";
import { GymList } from "./gym/GymList";
import { GymCreationSection } from "./gym/components/GymCreationSection";
import { GymHeader } from "./gym/components/GymHeader";
import { EmptyGymState } from "./gym/components/EmptyGymState";
import { GymLoadingState } from "./gym/components/GymLoadingState";
import { ReservationManagement } from "./reservations/ReservationManagement";
import { KioskoDownloadCard } from "./KioskoDownloadCard";
import { isNewBodyGym } from "@/utils/newBodyFeatures";
import { Gym } from "./gym/types";

export const GymManager = () => {
  const { data: gyms, isLoading } = useGymQuery();
  const {
    isEditing,
    setIsEditing,
    gymName,
    setGymName,
    gymAddress,
    setGymAddress,
    selectedGym,
    setSelectedGym,
  } = useGymState();
  const { createNewGym, updateGym, deleteGym } = useGymMutations();

  const [isCreating, setIsCreating] = useState(false);
  const [activeTab, setActiveTab] = useState("gyms");

  if (isLoading) return <GymLoadingState />;

  const handleCreateNew = () => {
    setIsCreating(true);
    setSelectedGym(null);
    setGymName("");
    setGymAddress("");
    setIsEditing(true);
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setGymName("");
    setGymAddress("");
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setSelectedGym(null);
  };

  const handleEdit = (gym: Gym) => {
    setSelectedGym(gym);
    setGymName(gym.name);
    setGymAddress(gym.address);
    setIsEditing(true);
  };

  const handleSave = async (gymId: string, name: string, address: string, logoFile: File | null) => {
    console.log("Saving gym:", { gymId, name, address });
    const success = await updateGym(gymId, name, address, logoFile);
    if (success) {
      setIsEditing(false);
      setSelectedGym(null);
    }
  };

  const handleCreateSave = async (name: string, address: string, logoFile: File | null) => {
    console.log("Creating gym with values:", { name, address });
    
    if (!name.trim() || !address.trim()) {
      console.error("Name and address are required");
      return;
    }
    
    const success = await createNewGym(name.trim(), address.trim(), logoFile);
    if (success) {
      setIsCreating(false);
      setGymName("");
      setGymAddress("");
    }
  };

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 gap-1">
          <TabsTrigger value="gyms" className="flex items-center gap-2 text-xs sm:text-sm">
            <Building2 className="h-4 w-4 shrink-0" />
            <span className="hidden sm:inline">Información del Gimnasio</span>
            <span className="sm:hidden">Gimnasio</span>
          </TabsTrigger>
          <TabsTrigger 
            value="reservations" 
            disabled={!gyms || gyms.length === 0}
            className="flex items-center gap-2 text-xs sm:text-sm"
          >
            <CalendarDays className="h-4 w-4 shrink-0" />
            <span className="hidden sm:inline">Gestión de Reservas</span>
            <span className="sm:hidden">Reservas</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="gyms" className="space-y-6">
          {gyms?.some((g) => isNewBodyGym(g.name)) && <KioskoDownloadCard />}
          <GymHeader onCreateNew={handleCreateNew} />

          {gyms?.length === 0 && !isCreating ? (
            <EmptyGymState />
          ) : (
            <>
              <GymCreationSection
                isCreating={isCreating}
                gymName={gymName}
                gymAddress={gymAddress}
                onSave={handleCreateSave}
                onCancel={handleCancelCreate}
              />

              <GymList
                gyms={gyms || []}
                selectedGym={selectedGym}
                isEditing={isEditing}
                gymName={gymName}
                gymAddress={gymAddress}
                onSave={handleSave}
                onCancel={handleCancelEdit}
                onEdit={handleEdit}
                onDelete={deleteGym}
              />
            </>
          )}
        </TabsContent>

        <TabsContent value="reservations" className="space-y-6">
          {gyms && gyms.length > 0 ? (
            <div className="space-y-6">
              {gyms.map((gym) => (
                <ReservationManagement key={gym.id} gym={gym} />
              ))}
            </div>
          ) : (
            <div className="text-center p-8">
              <p className="text-muted-foreground">
                Primero debes crear al menos un gimnasio para gestionar reservas.
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
